import {
  DataDictionary,
  HierarchyNode,
} from '../models/data-dictionary.model';
import { MasterTableItem } from './master-tables.service';
import { LogEntry } from './logs.service';

const now = new Date();

export const MOCK_HIERARCHY: HierarchyNode[] = [
  {
    id: 1,
    name: 'Clinical Data',
    type: 'category',
    parentId: null,
    order: 0,
    expanded: false,
    children: [
      {
        id: 2,
        name: 'Patient Records',
        type: 'category',
        parentId: 1,
        order: 0,
        expanded: false,
        children: [
          { id: 100, name: 'Patient Demographics', type: 'dictionary', parentId: 2, order: 0 },
          { id: 101, name: 'Patient Encounters', type: 'dictionary', parentId: 2, order: 1 },
        ]
      },
      {
        id: 3,
        name: 'Lab Results',
        type: 'category',
        parentId: 1,
        order: 1,
        expanded: false,
        children: [
          { id: 102, name: 'Blood Work', type: 'dictionary', parentId: 3, order: 0 },
        ]
      }
    ]
  },
  {
    id: 4,
    name: 'Administrative',
    type: 'category',
    parentId: null,
    order: 1,
    expanded: false,
    children: [
      { id: 103, name: 'Insurance Claims', type: 'dictionary', parentId: 4, order: 0 },
      { id: 104, name: 'Provider Directory', type: 'dictionary', parentId: 4, order: 1 },
    ]
  }
];

export const MOCK_DICTIONARIES: DataDictionary[] = [
  {
    id: 100,
    name: 'Patient Demographics',
    schema: 'clinical',
    subjectArea: 'Patient',
    description: 'Core patient demographic information including personal identifiers and contact details.',
    metadata: '',
    parentId: 2,
    createdAt: now,
    updatedAt: now,
    fields: [
      { id: 2001, position: 1, subPosition: 0, fieldName: 'patient_id', dataType: 'INTEGER', description: 'Unique patient identifier' },
      { id: 2002, position: 2, subPosition: 0, fieldName: 'first_name', dataType: 'VARCHAR', description: 'Patient first name' },
      { id: 2003, position: 3, subPosition: 0, fieldName: 'last_name', dataType: 'VARCHAR', description: 'Patient last name' },
      { id: 2004, position: 4, subPosition: 0, fieldName: 'date_of_birth', dataType: 'DATE', description: 'Patient date of birth' },
      { id: 2005, position: 5, subPosition: 0, fieldName: 'gender', dataType: 'CHAR', description: 'Patient gender code' },
      { id: 2006, position: 6, subPosition: 0, fieldName: 'email', dataType: 'VARCHAR', description: 'Patient email address' },
      { id: 2007, position: 7, subPosition: 0, fieldName: 'phone', dataType: 'VARCHAR', description: 'Patient phone number' },
      { id: 2008, position: 8, subPosition: 0, fieldName: 'address_line_1', dataType: 'TEXT', description: 'Primary address line' },
    ],
    sampleData: [
      { patient_id: 1001, first_name: 'John', last_name: 'Smith', date_of_birth: '1985-03-15', gender: 'M', email: 'john.smith@email.com', phone: '555-0101', address_line_1: '123 Main St' },
      { patient_id: 1002, first_name: 'Maria', last_name: 'Garcia', date_of_birth: '1990-07-22', gender: 'F', email: 'maria.garcia@email.com', phone: '555-0102', address_line_1: '456 Oak Ave' },
      { patient_id: 1003, first_name: 'James', last_name: 'Johnson', date_of_birth: '1978-11-08', gender: 'M', email: 'james.j@email.com', phone: '555-0103', address_line_1: '789 Pine Rd' },
    ]
  },
  {
    id: 101,
    name: 'Patient Encounters',
    schema: 'clinical',
    subjectArea: 'Encounter',
    description: 'Records of patient visits, admissions, and clinical encounters.',
    metadata: '',
    parentId: 2,
    createdAt: now,
    updatedAt: now,
    fields: [
      { id: 2020, position: 1, subPosition: 0, fieldName: 'encounter_id', dataType: 'UUID', description: 'Unique encounter identifier' },
      { id: 2021, position: 2, subPosition: 0, fieldName: 'patient_id', dataType: 'INTEGER', description: 'Reference to patient' },
      { id: 2022, position: 3, subPosition: 0, fieldName: 'encounter_date', dataType: 'DATETIME', description: 'Date and time of encounter' },
      { id: 2023, position: 4, subPosition: 0, fieldName: 'encounter_type', dataType: 'VARCHAR', description: 'Type of encounter (inpatient, outpatient)' },
      { id: 2024, position: 5, subPosition: 0, fieldName: 'provider_id', dataType: 'INTEGER', description: 'Attending provider' },
      { id: 2025, position: 6, subPosition: 0, fieldName: 'diagnosis_code', dataType: 'VARCHAR', description: 'ICD-10 diagnosis code' },
    ],
    sampleData: [
      { encounter_id: 'enc-001', patient_id: 1001, encounter_date: '2024-01-15 09:30:00', encounter_type: 'outpatient', provider_id: 501, diagnosis_code: 'J06.9' },
      { encounter_id: 'enc-002', patient_id: 1002, encounter_date: '2024-01-16 14:00:00', encounter_type: 'inpatient', provider_id: 502, diagnosis_code: 'I10' },
    ]
  },
  {
    id: 102,
    name: 'Blood Work',
    schema: 'lab',
    subjectArea: 'Laboratory',
    description: 'Blood test results and laboratory values.',
    metadata: '',
    parentId: 3,
    createdAt: now,
    updatedAt: now,
    fields: [
      { id: 2030, position: 1, subPosition: 0, fieldName: 'lab_id', dataType: 'UUID', description: 'Lab result identifier' },
      { id: 2031, position: 2, subPosition: 0, fieldName: 'patient_id', dataType: 'INTEGER', description: 'Patient reference' },
      { id: 2032, position: 3, subPosition: 0, fieldName: 'test_name', dataType: 'VARCHAR', description: 'Name of the lab test' },
      { id: 2033, position: 4, subPosition: 0, fieldName: 'result_value', dataType: 'DECIMAL', description: 'Numeric result value' },
      { id: 2034, position: 5, subPosition: 0, fieldName: 'result_unit', dataType: 'VARCHAR', description: 'Unit of measurement' },
      { id: 2035, position: 6, subPosition: 0, fieldName: 'is_abnormal', dataType: 'BOOLEAN', description: 'Flag for abnormal results' },
    ],
    sampleData: [
      { lab_id: 'lab-001', patient_id: 1001, test_name: 'Hemoglobin', result_value: 14.5, result_unit: 'g/dL', is_abnormal: false },
      { lab_id: 'lab-002', patient_id: 1001, test_name: 'WBC Count', result_value: 11200, result_unit: 'cells/uL', is_abnormal: true },
    ]
  },
  {
    id: 103,
    name: 'Insurance Claims',
    schema: 'admin',
    subjectArea: 'Claims',
    description: 'Insurance claim submissions and processing data.',
    metadata: '',
    parentId: 4,
    createdAt: now,
    updatedAt: now,
    fields: [
      { id: 2040, position: 1, subPosition: 0, fieldName: 'claim_id', dataType: 'VARCHAR', description: 'Unique claim identifier' },
      { id: 2041, position: 2, subPosition: 0, fieldName: 'patient_id', dataType: 'INTEGER', description: 'Patient reference' },
      { id: 2042, position: 3, subPosition: 0, fieldName: 'claim_date', dataType: 'DATE', description: 'Date claim was submitted' },
      { id: 2043, position: 4, subPosition: 0, fieldName: 'amount', dataType: 'DECIMAL', description: 'Claim amount in USD' },
      { id: 2044, position: 5, subPosition: 0, fieldName: 'status', dataType: 'VARCHAR', description: 'Claim processing status' },
    ],
    sampleData: [
      { claim_id: 'CLM-2024-001', patient_id: 1001, claim_date: '2024-01-20', amount: 1250.00, status: 'approved' },
      { claim_id: 'CLM-2024-002', patient_id: 1002, claim_date: '2024-01-21', amount: 3400.50, status: 'pending' },
    ]
  },
  {
    id: 104,
    name: 'Provider Directory',
    schema: 'admin',
    subjectArea: 'Provider',
    description: 'Healthcare provider information and credentials.',
    metadata: '',
    parentId: 4,
    createdAt: now,
    updatedAt: now,
    fields: [
      { id: 2050, position: 1, subPosition: 0, fieldName: 'provider_id', dataType: 'INTEGER', description: 'Provider unique ID' },
      { id: 2051, position: 2, subPosition: 0, fieldName: 'provider_name', dataType: 'VARCHAR', description: 'Full name of provider' },
      { id: 2052, position: 3, subPosition: 0, fieldName: 'specialty', dataType: 'VARCHAR', description: 'Medical specialty' },
      { id: 2053, position: 4, subPosition: 0, fieldName: 'npi', dataType: 'VARCHAR', description: 'National Provider Identifier' },
    ],
    sampleData: [
      { provider_id: 501, provider_name: 'Dr. Sarah Wilson', specialty: 'Cardiology', npi: '1234567890' },
      { provider_id: 502, provider_name: 'Dr. Michael Chen', specialty: 'Internal Medicine', npi: '0987654321' },
    ]
  }
];

export const MOCK_DATA_TYPES: MasterTableItem[] = [
  { id: 101, code: 'TEXT', label: 'Text', sortOrder: 0, createdAt: now, updatedAt: now },
  { id: 102, code: 'VARCHAR', label: 'Varchar', sortOrder: 1, createdAt: now, updatedAt: now },
  { id: 103, code: 'CHAR', label: 'Char', sortOrder: 2, createdAt: now, updatedAt: now },
  { id: 104, code: 'INTEGER', label: 'Integer', sortOrder: 3, createdAt: now, updatedAt: now },
  { id: 105, code: 'BIGINT', label: 'Bigint', sortOrder: 4, createdAt: now, updatedAt: now },
  { id: 106, code: 'SMALLINT', label: 'Smallint', sortOrder: 5, createdAt: now, updatedAt: now },
  { id: 107, code: 'DECIMAL', label: 'Decimal', sortOrder: 6, createdAt: now, updatedAt: now },
  { id: 108, code: 'NUMERIC', label: 'Numeric', sortOrder: 7, createdAt: now, updatedAt: now },
  { id: 109, code: 'FLOAT', label: 'Float', sortOrder: 8, createdAt: now, updatedAt: now },
  { id: 110, code: 'DOUBLE', label: 'Double', sortOrder: 9, createdAt: now, updatedAt: now },
  { id: 111, code: 'BOOLEAN', label: 'Boolean', sortOrder: 10, createdAt: now, updatedAt: now },
  { id: 112, code: 'DATE', label: 'Date', sortOrder: 11, createdAt: now, updatedAt: now },
  { id: 113, code: 'TIME', label: 'Time', sortOrder: 12, createdAt: now, updatedAt: now },
  { id: 114, code: 'TIMESTAMP', label: 'Timestamp', sortOrder: 13, createdAt: now, updatedAt: now },
  { id: 115, code: 'DATETIME', label: 'Datetime', sortOrder: 14, createdAt: now, updatedAt: now },
  { id: 116, code: 'BLOB', label: 'Blob', sortOrder: 15, createdAt: now, updatedAt: now },
  { id: 117, code: 'CLOB', label: 'Clob', sortOrder: 16, createdAt: now, updatedAt: now },
  { id: 118, code: 'JSON', label: 'JSON', sortOrder: 17, createdAt: now, updatedAt: now },
  { id: 119, code: 'UUID', label: 'UUID', sortOrder: 18, createdAt: now, updatedAt: now }
];

export const MOCK_LOGS: LogEntry[] = [
  { id: 9001, timestamp: new Date(Date.now() - 3600000), action: 'CREATE', entity: 'Dictionary', entityId: '100', user: 'Admin User', description: 'Created dictionary "Patient Demographics"' },
  { id: 9002, timestamp: new Date(Date.now() - 3000000), action: 'CREATE', entity: 'Dictionary', entityId: '101', user: 'Admin User', description: 'Created dictionary "Patient Encounters"' },
  { id: 9003, timestamp: new Date(Date.now() - 2400000), action: 'UPDATE', entity: 'Field', entityId: '2005', user: 'Admin User', description: 'Updated field "gender" in Patient Demographics' },
  { id: 9004, timestamp: new Date(Date.now() - 1800000), action: 'IMPORT', entity: 'Dictionary', entityId: '102', user: 'Admin User', description: 'Imported fields into "Blood Work"' },
  { id: 9005, timestamp: new Date(Date.now() - 1200000), action: 'EXPORT', entity: 'Dictionary', entityId: '103', user: 'Admin User', description: 'Exported "Insurance Claims" to Excel' },
  { id: 9006, timestamp: new Date(Date.now() - 600000), action: 'DELETE', entity: 'Field', entityId: '2099', user: 'Admin User', description: 'Deleted field "obsolete_field" from Provider Directory' },
];

let _mockIdSeq = 10_000;

/** Next synthetic numeric id for mock data (API uses MySQL auto-increment). */
export function generateMockId(_prefix: string): number {
  _mockIdSeq += 1;
  return _mockIdSeq;
}
